import { ReactNode } from "react";
import { useId } from "react-aria";

import useAnalytics, { AnalyticsCommonProps } from "hooks/useAnalytics";
import Box from "ds/components/Box";
import Typography from "ds/components/Typography";
import TooltipInfo, { TooltipInfoVariant } from "ds/components/TooltipInfo";
import Toggle from "ds/components/Toggle";
import { FlexAlign } from "types/Flex";
import { TooltipProps } from "ds/components/Tooltip/types";

import { getColors } from "../Field/utils";
import styles from "./styles.module.css";

export type ToggleFieldProps = {
  color?: "default" | "inversed";
  variant: "switch" | "checkbox";
  onChange: (isSelected: boolean) => void;
  checked: boolean;
  title: string;
  description?: string;
  tooltip?: (isSelected: boolean) => string;
  tooltipInfo?: ReactNode;
  tooltipInfoVariant?: TooltipInfoVariant;
  tooltipWidthMode?: TooltipProps["widthMode"];
  titleSuffix?: ReactNode; // TODO: remove tooltipInfo, use suffix instead
  tooltipAnalyticsTitle?: string;
  disabled?: boolean;
  titleId?: string;
  align?: FlexAlign;
  id?: string;
  isIndeterminate?: boolean;
} & AnalyticsCommonProps;

const FormToggleField = ({
  onChange,
  checked,
  title,
  titleId,
  description,
  titleSuffix,
  tooltip,
  tooltipInfo,
  tooltipInfoVariant = "modal",
  tooltipWidthMode,
  tooltipAnalyticsTitle,
  analyticsPage,
  analyticsProps,
  analyticsTitle,
  align = "start",
  variant,
  color = "default",
  disabled,
  id,
  isIndeterminate,
}: ToggleFieldProps) => {
  const descriptionId = useId();
  const trackSegmentEvent = useAnalytics({
    page: analyticsPage,
  });

  const onChangeHandler = (isSelected: boolean) => {
    onChange(isSelected);

    if (analyticsTitle && analyticsPage) {
      trackSegmentEvent?.(analyticsTitle, { ...analyticsProps, checked: isSelected });
    }
  };
  const { primaryIconColor } = getColors(color);
  return (
    <Toggle
      id={id}
      color={color}
      variant={variant}
      onChange={onChangeHandler}
      isSelected={checked}
      isDisabled={disabled}
      tooltip={tooltip}
      align={align}
      aria-label={title}
      aria-describedby={descriptionId}
      isIndeterminate={isIndeterminate}
    >
      <Box direction="column">
        <Box className={styles.label} gap="small" align="center">
          <Typography className={styles.labelText} tag="p" id={titleId}>
            {title}
          </Typography>
          {tooltipInfo && (
            <Box align="center">
              <TooltipInfo
                analyticsPage={analyticsPage}
                analyticsTitle={tooltipAnalyticsTitle}
                analyticsProps={analyticsProps}
                variant={tooltipInfoVariant}
                iconColor={primaryIconColor}
                widthMode={tooltipWidthMode}
              >
                {tooltipInfo}
              </TooltipInfo>
            </Box>
          )}
          {titleSuffix}
        </Box>

        <Typography id={descriptionId} tag="p" variant="p-body4" color="secondary">
          {description}
        </Typography>
      </Box>
    </Toggle>
  );
};

FormToggleField.displayName = "DS.Form.ToggleField";

export default FormToggleField;
